import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useCart, useClient, useTables } from '@/hooks';
import { CartItems } from '@/hooks/CartContext';
import { useDbFunctions } from '../dbFunctions/useDBFunctions';
import {
    buildComboCartItemsMapWithSectionDecrement,
} from './comboCartSectionDecrement';
import {
    ActiveComboInCart,
    buildComboKitStatusFromInstances,
    buildComboProgress,
    buildNextKitProgress,
    ComboCartItemAllocation,
    ComboCartItemsMap,
    ComboKitStatus,
    getActiveCompleteKitCount,
    getActiveCombosInCart,
    getComboDiscountParticipatingQuantityForCartItem,
    getComboParticipatingQuantityForCartItem,
    isComboComplete,
} from './comboCartUtils';
import { ComboInstanceStatus, getComboInstanceStatuses } from './comboCartInstances';
import {
    ensureComboDataLoaded,
    getComboDataSnapshot,
    getNormalizedCombosForStore,
    subscribeComboData,
} from './comboDataLoader';

export const useComboCartItems = () => {
    const { getOnDB } = useDbFunctions();
    const { tables } = useTables();
    const { selectedClient } = useClient();
    const { currentCartDetails } = useCart();
    const combosFromDb = useSyncExternalStore(subscribeComboData, getComboDataSnapshot, getComboDataSnapshot);

    useEffect(() => {
        void ensureComboDataLoaded(getOnDB, tables);
    }, [getOnDB, tables]);

    const cartItems = useMemo(() => currentCartDetails?.items ?? [], [currentCartDetails?.items]);

    const combos = useMemo(() => {
        const source =
            Array.isArray(combosFromDb) && combosFromDb.length > 0 ? combosFromDb : tables.current.combo;

        return getNormalizedCombosForStore(source, selectedClient?.COD_LOJA);
    }, [combosFromDb, selectedClient?.COD_LOJA]);

    const activeCombosInCart = useMemo(
        () => getActiveCombosInCart(combos, cartItems),
        [combos, cartItems]
    );

    const comboCartItemsByUid = useMemo(() => {
        return buildComboCartItemsMapWithSectionDecrement(cartItems, combos);
    }, [cartItems, combos]);

    const comboKitStatusById = useMemo(() => {
        const map: Record<string, ComboKitStatus> = {};

        combos.forEach((combo: { id_campanha: string }) => {
            map[combo.id_campanha] = buildComboKitStatusFromInstances(
                combo,
                cartItems,
                (uid) => comboCartItemsByUid[uid]
            );
        });

        return map;
    }, [combos, cartItems, comboCartItemsByUid]);

    const comboInstanceStatusesById = useMemo(() => {
        const map: Record<string, ComboInstanceStatus[]> = {};

        combos.forEach((combo: { id_campanha: string }) => {
            map[combo.id_campanha] = getComboInstanceStatuses(
                combo,
                cartItems,
                (uid) => comboCartItemsByUid[uid]
            );
        });

        return map;
    }, [combos, cartItems, comboCartItemsByUid]);

    const getComboKitStatusById = useCallback(
        (idCampanha: string) => comboKitStatusById[idCampanha],
        [comboKitStatusById]
    );

    const getComboInstanceStatusesById = useCallback(
        (idCampanha: string) => comboInstanceStatusesById[idCampanha] ?? [],
        [comboInstanceStatusesById]
    );

    const getActiveCompleteKitCountById = useCallback(
        (idCampanha: string) => {
            const combo = combos.find((item: { id_campanha: string }) => item.id_campanha === idCampanha);
            if (!combo) return 0;
            return getActiveCompleteKitCount(combo, cartItems);
        },
        [combos, cartItems]
    );

    const getComboCartItemInfo = useCallback(
        (uid: string): ComboCartItemAllocation[] | undefined => comboCartItemsByUid[uid],
        [comboCartItemsByUid]
    );

    const isCartItemInCombo = useCallback(
        (uid: string) => Boolean(comboCartItemsByUid[uid]?.length),
        [comboCartItemsByUid]
    );

    const isComboCompleteById = useCallback(
        (idCampanha: string) => {
            const combo = combos.find((item: { id_campanha: string }) => item.id_campanha === idCampanha);
            if (!combo) return false;
            return isComboComplete(combo, cartItems);
        },
        [combos, cartItems]
    );

    const getComboProgressById = useCallback(
        (idCampanha: string) => {
            const combo = combos.find((item: { id_campanha: string }) => item.id_campanha === idCampanha);
            if (!combo) return null;

            const kitStatus = buildComboKitStatusFromInstances(
                combo,
                cartItems,
                (uid) => comboCartItemsByUid[uid]
            );

            if (kitStatus.activeCompleteKits > 0 && kitStatus.hasInProgressKit) {
                return buildNextKitProgress(combo, cartItems, kitStatus.activeCompleteKits);
            }

            return buildComboProgress(combo, cartItems);
        },
        [combos, cartItems, comboCartItemsByUid]
    );

    const getComboDiscountParticipatingQuantityForItem = useCallback(
        (item: Pick<CartItems, 'uid' | 'sku' | 'option_uid' | 'quantity'>) =>
            getComboDiscountParticipatingQuantityForCartItem(item, getComboCartItemInfo(item.uid)),
        [getComboCartItemInfo]
    );

    const getComboParticipatingQuantityForItem = useCallback(
        (item: Pick<CartItems, 'uid' | 'sku' | 'option_uid' | 'quantity'>) =>
            getComboParticipatingQuantityForCartItem(item, getComboCartItemInfo(item.uid)),
        [getComboCartItemInfo]
    );

    return {
        combos,
        cartItems,
        activeCombosInCart: activeCombosInCart as ActiveComboInCart[],
        comboCartItemsByUid: comboCartItemsByUid as ComboCartItemsMap,
        getComboCartItemInfo,
        getComboDiscountParticipatingQuantityForItem,
        getComboParticipatingQuantityForItem,
        getComboKitStatusById,
        getComboInstanceStatusesById,
        getActiveCompleteKitCountById,
        isCartItemInCombo,
        isComboCompleteById,
        getComboProgressById,
    };
};
